import type { DatabaseLike } from '../db/types';
import {
  buildVerseSearchExpression,
  buildVerseSearchQuery,
  CATALOG_QUERY,
  CHAPTER_VERSES_QUERY,
  CROSS_REFERENCES_QUERY,
  GREEK_SOURCE_QUERY,
  HEBREW_SOURCE_QUERY,
  LXX_SOURCE_QUERY,
} from './queries';
import { ReaderStreamState } from './stream-state.svelte';
import {
  buildHornerChapterListStreamInput,
  HORNER_SEQUENCE_SOURCE,
  hornerReadKey,
  resolveHornerReadingsForDay,
} from './horner-plan';
import {
  buildCanonicalStreamSequence,
  buildFixedListStreamSequence,
  chapterKey,
} from './stream-sequence';
import { READER_WORKSPACE_SNAPSHOT_VERSION, STREAM_PRIME_RADIUS } from './types';
import type {
  BookSummary,
  ChapterListStreamInput,
  ChapterData,
  ChapterRef,
  CrossReference,
  HornerProgressState,
  NavigationTarget,
  NavHandedness,
  ReaderHistoryState,
  ReaderStreamSequence,
  ReaderStreamSnapshot,
  ReaderWorkspaceSnapshot,
  ScrollAlign,
  SearchResult,
  Testament,
  UpdateCheckState,
  Verse,
} from './types';
import {
  ReaderWorkspaceStorage,
  type ReaderHornerProgressStore,
  type ReaderWorkspaceStore,
} from './workspace-storage';

interface ChapterCatalogRow {
  book_id: number;
  book_name: string;
  testament: Testament;
  chapter: number;
  verse_count: number;
}

interface VerseRow {
  verse: number;
  text: string;
}

interface SourceWordRow {
  verse: number;
  word_num: number;
  text: string;
}

interface CrossReferenceRow {
  id: number;
  from_verse: number;
  to_book_id: number;
  to_book_name: string;
  to_chapter: number;
  to_verse: number;
  to_verse_end: number | null;
  votes: number;
  target_verse: number | null;
  target_text: string | null;
}

interface SearchRow {
  book_id: number;
  book_name: string;
  chapter: number;
  verse: number;
  text: string;
}

interface InitialStreamTarget {
  sequence: ReaderStreamSequence;
  itemIndex: number;
  catalogIndex: number;
  align: ScrollAlign;
  restoreScrollTop: number | null;
  streamId: string;
}

interface InitialWorkspaceState {
  streams: InitialStreamTarget[];
  activeStreamIndex: number;
}

const CACHE_RADIUS = STREAM_PRIME_RADIUS * 3 + 2;
const CACHE_PRUNE_HEADROOM = STREAM_PRIME_RADIUS * 2;
const MAX_OPEN_STREAMS = 8;

export class ReaderState {
  phase = $state<'booting' | 'ready'>('booting');
  books = $state.raw<BookSummary[]>([]);
  chapterRefs = $state.raw<ChapterRef[]>([]);
  streams = $state.raw<ReaderStreamState[]>([]);
  activeStreamIndex = $state(0);
  loadedChapters = $state.raw(new Map<number, ChapterData>());
  loadingChapters = $state.raw(new Set<number>());
  chapterError = $state<string | null>(null);
  navigatorOpen = $state(false);
  draftTestament = $state<Testament>('NT');
  draftBookId = $state<number | null>(null);
  navHandedness = $state<NavHandedness>(this.readStoredNavHandedness());
  updateStatus = $state<UpdateCheckState>({ state: 'idle' });
  settingsOpen = $state(false);
  searchOpen = $state(false);
  searchQuery = $state('');
  searchResults = $state.raw<SearchResult[]>([]);
  searchBusy = $state(false);
  searchError = $state<string | null>(null);
  hornerDay = $state(1);
  hornerReadKeys = $state.raw(new Set<string>());

  private readonly chapterIndexByKey = new Map<string, number>();
  private readonly chapterLoads = new Map<number, Promise<void>>();
  private readonly navHandednessStorageKey = 'kjvreader.nav-handedness';
  private searchRequestId = 0;
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private updateStatusClearHandle: ReturnType<typeof setTimeout> | null = null;
  private persistHandle: ReturnType<typeof setTimeout> | null = null;
  private pruneHandle: ReturnType<typeof setTimeout> | null = null;
  private streamIdNonce = 0;

  constructor(
    private readonly db: DatabaseLike,
    private readonly storage: ReaderWorkspaceStore & Partial<ReaderHornerProgressStore> =
      new ReaderWorkspaceStorage(),
  ) {}

  get activeStream(): ReaderStreamState | null {
    return this.streams[this.activeStreamIndex] ?? null;
  }

  get currentReference(): ChapterRef | null {
    return this.activeStream?.currentReference ?? null;
  }

  get currentBook(): BookSummary | null {
    const bookId = this.currentReference?.bookId;
    return this.books.find((book) => book.id === bookId) ?? null;
  }

  get currentChapterNumber(): number {
    return this.currentReference?.chapter ?? 1;
  }

  get chapterLoading(): boolean {
    const index = this.activeStream?.currentCatalogIndex ?? null;
    return index !== null && this.loadingChapters.has(index);
  }

  get draftBooks(): BookSummary[] {
    return this.books.filter((book) => book.testament === this.draftTestament);
  }

  get draftBook(): BookSummary | null {
    return (
      this.draftBooks.find((book) => book.id === this.draftBookId) ??
      this.draftBooks[0] ??
      this.books[0] ??
      null
    );
  }

  get draftChapters(): number[] {
    const book = this.draftBook;
    return book ? Array.from({ length: book.chapterCount }, (_, index) => index + 1) : [];
  }

  get activeHornerDay(): number | null {
    return this.isHornerStream(this.activeStream) ? this.hornerDay : null;
  }

  async init(): Promise<void> {
    try {
      await this.db.init();
      await this.loadCatalog();
      await this.loadHornerProgress();

      const persistedSnapshot = await this.readPersistedSnapshot();
      const initialWorkspace = this.resolveInitialWorkspace(persistedSnapshot);
      const streams = initialWorkspace.streams.map((target) =>
        this.createStream(
          target.sequence,
          target.itemIndex,
          target.restoreScrollTop ?? 0,
          target.streamId,
        ),
      );
      const activeTarget = initialWorkspace.streams[initialWorkspace.activeStreamIndex];
      const activeReference = activeTarget ? this.chapterRefs[activeTarget.catalogIndex] : null;
      if (!activeReference || streams.length === 0) {
        throw new Error('The Bible database did not return any chapters.');
      }

      this.streams = streams;
      this.activeStreamIndex = initialWorkspace.activeStreamIndex;

      for (const [streamIndex, stream] of streams.entries()) {
        const target = initialWorkspace.streams[streamIndex];
        if (!target) continue;

        stream.activateItemIndex(target.itemIndex);
        stream.requestNavigation(
          { itemIndex: target.itemIndex, align: target.align, targetVerse: null },
          'auto',
          { restoreScrollTop: target.restoreScrollTop },
        );
      }

      this.resetDraftSelection(activeReference.bookId);
      this.replaceLocationToReference(activeReference, {
        targetVerse: null,
        align: activeTarget.align,
      });
      this.phase = 'ready';
      this.schedulePersistWorkspace();
    } catch (error) {
      this.chapterError = this.toErrorMessage(error);
    }
  }

  destroy(): void {
    if (this.searchDebounceHandle !== null) {
      clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = null;
    }

    if (this.updateStatusClearHandle !== null) {
      clearTimeout(this.updateStatusClearHandle);
      this.updateStatusClearHandle = null;
    }

    if (this.persistHandle !== null) {
      clearTimeout(this.persistHandle);
      this.persistHandle = null;
    }

    if (this.pruneHandle !== null) {
      clearTimeout(this.pruneHandle);
      this.pruneHandle = null;
    }

    this.flushWorkspacePersistence();

    for (const stream of this.streams) {
      stream.destroy();
    }
  }

  toggleNavigator(): void {
    this.navigatorOpen ? this.closeNavigator() : this.openNavigator();
  }

  openNavigator(): void {
    this.closeSettingsMenu();
    this.closeSearchWidget();
    this.resetDraftSelection(this.currentReference?.bookId ?? null);
    this.navigatorOpen = true;
  }

  closeNavigator(): void {
    this.navigatorOpen = false;
  }

  toggleSettingsMenu(): void {
    this.settingsOpen ? this.closeSettingsMenu() : this.openSettingsMenu();
  }

  openSettingsMenu(): void {
    this.closeNavigator();
    this.closeSearchWidget();
    this.settingsOpen = true;
  }

  closeSettingsMenu(): void {
    this.settingsOpen = false;
  }

  openSearchWidget(): void {
    this.closeNavigator();
    this.closeSettingsMenu();
    this.searchOpen = true;
  }

  closeSearchWidget(): void {
    this.searchOpen = false;
  }

  chooseTestament(testament: Testament): void {
    this.draftTestament = testament;
    const currentBook = this.books.find((book) => book.id === this.draftBookId);
    if (currentBook?.testament === testament) {
      return;
    }

    this.draftBookId = this.books.find((book) => book.testament === testament)?.id ?? null;
  }

  chooseBook(book: BookSummary): void {
    this.draftTestament = book.testament;
    this.draftBookId = book.id;
  }

  chooseChapter(chapter: number): NavigationTarget | null {
    const book = this.draftBook;
    const stream = this.activeStream;
    if (!book || !stream) return null;

    const index = this.chapterIndexByKey.get(chapterKey(book.id, chapter));
    if (index === undefined) return null;

    const reference = this.chapterRefs[index];
    if (!reference) return null;

    const itemIndex = this.prepareStreamForCanonicalTarget(stream, index);
    const target = { itemIndex, align: 'center' as const, targetVerse: null };
    stream.requestNavigation(target, 'auto');
    this.pushLocationToReference(reference, target);
    this.resetDraftSelection(reference.bookId);
    this.navigatorOpen = false;
    this.chapterError = null;
    this.schedulePersistWorkspace();
    return target;
  }

  activateFromHistory(state: unknown): NavigationTarget | null {
    const target = this.historyTargetFromState(state) ?? this.historyTargetFromLocation();
    if (!target) return null;

    return this.restoreWorkspaceFromHistory(target);
  }

  activateStreamFromScroll(streamId: string, itemIndex: number): void {
    const streamIndex = this.streams.findIndex((stream) => stream.id === streamId);
    if (streamIndex === -1 || streamIndex !== this.activeStreamIndex) return;

    const stream = this.streams[streamIndex];
    if (!stream) return;

    const catalogIndex = stream.sequence.items[itemIndex]?.catalogIndex;
    if (catalogIndex === undefined) return;

    const reference = this.chapterRefs[catalogIndex];
    if (!reference) return;

    if (streamIndex !== this.activeStreamIndex) {
      this.activeStreamIndex = streamIndex;
    }

    stream.activateFromScroll(itemIndex);
    this.resetDraftSelection(reference.bookId);
    this.replaceLocationToReference(reference, { targetVerse: null, align: 'center' });
    this.schedulePersistWorkspace();
    this.schedulePruneChapterCache();
  }

  activateCarouselStream(index: number): boolean {
    const stream = this.streams[index];
    if (!stream || index === this.activeStreamIndex) {
      return false;
    }

    const reference = stream.currentReference;
    const currentItemIndex = stream.currentItemIndex;
    if (!reference) {
      return false;
    }

    this.activeStreamIndex = index;
    if (currentItemIndex !== null) {
      stream.requestNavigation(
        {
          itemIndex: currentItemIndex,
          align: 'center',
          targetVerse: null,
        },
        'auto',
        {
          restoreScrollTop: stream.scrollTop,
        },
      );
    }
    this.resetDraftSelection(reference.bookId);
    this.replaceLocationToReference(reference, { targetVerse: null, align: 'center' });
    this.schedulePersistWorkspace();
    return true;
  }

  moveActiveStream(delta: -1 | 1): boolean {
    const nextIndex = this.activeStreamIndex + delta;
    if (nextIndex < 0 || nextIndex >= this.streams.length) {
      return false;
    }

    return this.activateCarouselStream(nextIndex);
  }

  openStreamRight(
    bookId: number,
    chapter: number,
    options: {
      targetVerse?: number | null;
      align?: ScrollAlign;
      history?: 'push' | 'replace' | 'none';
      restoreScrollTop?: number | null;
      preferredId?: string;
    } = {},
  ): NavigationTarget | null {
    if (this.streams.length >= MAX_OPEN_STREAMS) {
      return null;
    }

    const index = this.chapterIndexByKey.get(chapterKey(bookId, chapter));
    if (index === undefined) return null;

    const reference = this.chapterRefs[index];
    if (!reference) return null;

    const stream = this.createStream(
      this.canonicalSequence(),
      index,
      options.restoreScrollTop ?? 0,
      options.preferredId,
    );
    const insertionIndex = Math.min(this.activeStreamIndex + 1, this.streams.length);
    const nextStreams = [...this.streams];
    nextStreams.splice(insertionIndex, 0, stream);
    this.streams = nextStreams;
    this.activeStreamIndex = insertionIndex;

    const target = {
      itemIndex: index,
      align:
        options.align ??
        (options.targetVerse === null || options.targetVerse === undefined ? 'center' : 'top'),
      targetVerse: options.targetVerse ?? null,
    } satisfies NavigationTarget;

    stream.activateItemIndex(index);
    if (target.targetVerse !== null) {
      stream.highlightVerse(bookId, chapter, target.targetVerse);
    }
    stream.requestNavigation(target, 'auto', {
      restoreScrollTop: options.restoreScrollTop ?? null,
    });

    this.resetDraftSelection(bookId);
    if (options.history === 'push') {
      this.pushLocationToReference(reference, target);
    } else if (options.history !== 'none') {
      this.replaceLocationToReference(reference, target);
    }

    this.schedulePersistWorkspace();
    this.schedulePruneChapterCache();
    return target;
  }

  openChapterListStreamRight(
    items: ChapterListStreamInput[],
    options: {
      title?: string | null;
      source?: string | null;
      history?: 'push' | 'replace' | 'none';
      restoreScrollTop?: number | null;
      preferredId?: string;
    } = {},
  ): NavigationTarget | null {
    if (this.streams.length >= MAX_OPEN_STREAMS) {
      return null;
    }

    const sequence = buildFixedListStreamSequence(this.chapterRefs, this.chapterIndexByKey, {
      title: options.title ?? null,
      source: options.source ?? null,
      items,
    });
    if (!sequence) return null;

    const firstItem = sequence.items[0];
    const reference = firstItem ? this.chapterRefs[firstItem.catalogIndex] : null;
    if (!firstItem || !reference) return null;

    const stream = this.createStream(
      sequence,
      0,
      options.restoreScrollTop ?? 0,
      options.preferredId,
    );
    const insertionIndex = Math.min(this.activeStreamIndex + 1, this.streams.length);
    const nextStreams = [...this.streams];
    nextStreams.splice(insertionIndex, 0, stream);
    this.streams = nextStreams;
    this.activeStreamIndex = insertionIndex;

    const target = {
      itemIndex: 0,
      align: 'top' as const,
      targetVerse: null,
    } satisfies NavigationTarget;

    stream.activateItemIndex(0);
    stream.requestNavigation(target, 'auto', {
      restoreScrollTop: options.restoreScrollTop ?? null,
    });

    this.resetDraftSelection(reference.bookId);
    if (options.history === 'push') {
      this.pushLocationToReference(reference, target);
    } else if (options.history !== 'none') {
      this.replaceLocationToReference(reference, target);
    }

    this.schedulePersistWorkspace();
    this.schedulePruneChapterCache();
    return target;
  }

  openHornerReading(): NavigationTarget | null {
    const sequence = this.buildHornerSequence();
    if (!sequence) return null;

    const existingIndex = this.streams.findIndex((stream) => this.isHornerStream(stream));
    if (existingIndex >= 0) {
      const stream = this.streams[existingIndex];
      if (!stream) return null;

      this.activeStreamIndex = existingIndex;
      return this.replaceHornerStreamSequence(stream);
    }

    return this.openChapterListStreamRight(this.hornerInputForCurrentDay(), {
      title: this.hornerTitle(),
      source: HORNER_SEQUENCE_SOURCE,
      history: 'replace',
    });
  }

  isHornerStream(stream: ReaderStreamState | null | undefined): boolean {
    return stream?.sequence.source === HORNER_SEQUENCE_SOURCE;
  }

  isHornerChapterRead(bookId: number, chapter: number): boolean {
    return this.hornerReadKeys.has(this.hornerKey(bookId, chapter));
  }

  toggleHornerChapterRead(streamId: string, bookId: number, chapter: number): void {
    const stream = this.streams.find((entry) => entry.id === streamId);
    if (!stream || !this.isHornerStream(stream)) {
      return;
    }

    const key = this.hornerKey(bookId, chapter);
    const nextReadKeys = new Set(this.hornerReadKeys);
    if (nextReadKeys.has(key)) {
      nextReadKeys.delete(key);
      this.hornerReadKeys = nextReadKeys;
      this.persistHornerProgress();
      return;
    }

    nextReadKeys.add(key);
    if (this.hornerDayIsComplete(nextReadKeys)) {
      this.hornerDay += 1;
      this.hornerReadKeys = new Set();
      this.replaceHornerStreamSequence(stream);
    } else {
      this.hornerReadKeys = nextReadKeys;
    }

    this.persistHornerProgress();
  }

  setHornerReadingDay(day: number): NavigationTarget | null {
    if (!Number.isInteger(day) || day < 1) {
      return null;
    }

    this.hornerDay = day;
    this.hornerReadKeys = new Set();
    this.persistHornerProgress();

    const streamIndex = this.streams.findIndex((stream) => this.isHornerStream(stream));
    const stream = this.streams[streamIndex];
    if (!stream) {
      return null;
    }

    this.activeStreamIndex = streamIndex;
    return this.replaceHornerStreamSequence(stream);
  }

  closeStream(streamId: string): boolean {
    const streamIndex = this.streams.findIndex((stream) => stream.id === streamId);
    if (streamIndex <= 0) {
      return false;
    }

    const stream = this.streams[streamIndex];
    if (!stream) {
      return false;
    }

    const nextStreams = [...this.streams];
    nextStreams.splice(streamIndex, 1);
    stream.destroy();

    const nextActiveStreamIndex =
      streamIndex < this.activeStreamIndex
        ? this.activeStreamIndex - 1
        : streamIndex === this.activeStreamIndex
          ? Math.min(streamIndex, nextStreams.length - 1)
          : this.activeStreamIndex;

    this.streams = nextStreams;
    this.activeStreamIndex = Math.max(0, nextActiveStreamIndex);

    const activeReference = this.activeStream?.currentReference;
    if (activeReference) {
      this.resetDraftSelection(activeReference.bookId);
      this.replaceLocationToReference(activeReference, { targetVerse: null, align: 'center' });
    }

    this.schedulePersistWorkspace();
    this.schedulePruneChapterCache();
    return true;
  }

  navigateToVerse(
    bookId: number,
    chapter: number,
    verse: number,
    options: { closeSearch?: boolean } = {},
  ): NavigationTarget | null {
    const stream = this.activeStream;
    if (!stream) return null;

    const index = this.chapterIndexByKey.get(chapterKey(bookId, chapter));
    if (index === undefined) return null;

    const reference = this.chapterRefs[index];
    if (!reference) return null;

    if (options.closeSearch) {
      this.closeSearchWidget();
    }

    const itemIndex = this.prepareStreamForCanonicalTarget(stream, index);
    const target = { itemIndex, align: 'top' as const, targetVerse: verse };
    stream.highlightVerse(bookId, chapter, verse);
    stream.requestNavigation(target, 'auto');
    this.pushLocationToReference(reference, target);
    this.resetDraftSelection(bookId);
    this.chapterError = null;
    this.schedulePersistWorkspace();
    return target;
  }

  scheduleWorkspacePersistence(delay = 120): void {
    this.schedulePersistWorkspace(delay);
  }

  flushWorkspacePersistence(): void {
    if (this.persistHandle !== null) {
      clearTimeout(this.persistHandle);
      this.persistHandle = null;
    }

    void this.persistWorkspace();
  }

  updateSearchQuery(value: string): void {
    const nextQuery = value;
    this.searchQuery = nextQuery;
    this.searchError = null;
    this.searchOpen = true;

    if (this.searchDebounceHandle !== null) {
      clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = null;
    }

    const trimmedQuery = nextQuery.trim();
    this.searchRequestId += 1;

    if (trimmedQuery.length < 2) {
      this.searchBusy = false;
      this.searchResults = [];
      return;
    }

    const requestId = this.searchRequestId;
    this.searchBusy = true;
    this.searchDebounceHandle = setTimeout(() => {
      this.searchDebounceHandle = null;
      void this.runVerseSearch(trimmedQuery, requestId);
    }, 200);
  }

  clearSearch(options: { keepOpen?: boolean } = {}): void {
    if (this.searchDebounceHandle !== null) {
      clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = null;
    }

    this.searchRequestId += 1;
    this.searchQuery = '';
    this.searchResults = [];
    this.searchBusy = false;
    this.searchError = null;
    this.searchOpen = options.keepOpen ?? true;
  }

  toggleNavHandedness(): void {
    this.navHandedness = this.navHandedness === 'right' ? 'left' : 'right';
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.navHandednessStorageKey, this.navHandedness);
    }
  }

  async checkForUpdates(checkForAppUpdate: () => Promise<boolean>): Promise<void> {
    if (this.updateStatus.state === 'checking') {
      return;
    }

    this.setUpdateStatus({ state: 'checking' });

    try {
      const [appUpdated, dbUpdated] = await Promise.all([
        checkForAppUpdate(),
        this.db.checkForUpdates(),
      ]);

      if (appUpdated || dbUpdated) {
        if (typeof location !== 'undefined') {
          location.reload();
        }
        return;
      }

      this.setUpdateStatus({ state: 'up-to-date' });
    } catch (error) {
      this.setUpdateStatus({
        state: 'error',
        message: this.toErrorMessage(error),
      });
    }
  }

  slotHeight(index: number, measuredHeight: number | undefined): number {
    if (measuredHeight) return measuredHeight;

    const reference = this.chapterRefs[index];
    if (!reference) return 360;

    const loadedChapter = this.loadedChapters.get(index);
    const verseCount = loadedChapter?.verses.length ?? reference.verseCount;
    return Math.max(360, verseCount * 38 + 180);
  }

  chapterAt(index: number): ChapterData | null {
    return this.loadedChapters.get(index) ?? null;
  }

  chapterRefAt(index: number): ChapterRef | null {
    return this.chapterRefs[index] ?? null;
  }

  async ensureChapterLoaded(index: number): Promise<void> {
    if (this.loadedChapters.has(index)) {
      return;
    }

    const existingLoad = this.chapterLoads.get(index);
    if (existingLoad) {
      await existingLoad;
      return;
    }

    const reference = this.chapterRefs[index];
    if (!reference) return;

    this.loadingChapters = new Set([...this.loadingChapters, index]);

    const load = (async () => {
      try {
        const [verses, greekRows, hebrewRows, lxxRows, crossReferenceRows] = await Promise.all([
          this.db.query<VerseRow>(CHAPTER_VERSES_QUERY, [reference.bookId, reference.chapter]),
          reference.testament === 'NT'
            ? this.db.query<SourceWordRow>(GREEK_SOURCE_QUERY, [
                reference.bookId,
                reference.chapter,
              ])
            : Promise.resolve([]),
          reference.testament === 'OT'
            ? this.db.query<SourceWordRow>(HEBREW_SOURCE_QUERY, [
                reference.bookId,
                reference.chapter,
              ])
            : Promise.resolve([]),
          reference.testament === 'OT'
            ? this.db.query<SourceWordRow>(LXX_SOURCE_QUERY, [reference.bookId, reference.chapter])
            : Promise.resolve([]),
          this.db.query<CrossReferenceRow>(CROSS_REFERENCES_QUERY, [
            reference.bookId,
            reference.chapter,
          ]),
        ]);

        const greekByVerse = this.groupSourceRowsByVerse(greekRows);
        const hebrewByVerse = this.groupSourceRowsByVerse(hebrewRows, (token) =>
          token.replace(/\//g, ''),
        );
        const lxxByVerse = this.groupSourceRowsByVerse(lxxRows);
        const crossReferencesByVerse = this.groupCrossReferenceRowsByVerse(crossReferenceRows);

        const nextLoaded = new Map(this.loadedChapters);
        nextLoaded.set(index, {
          ...reference,
          verses: verses.map<Verse>((verse) => ({
            verse: Number(verse.verse),
            text: String(verse.text),
            greekText: greekByVerse.get(Number(verse.verse)) ?? null,
            hebrewText: hebrewByVerse.get(Number(verse.verse)) ?? null,
            lxxText: lxxByVerse.get(Number(verse.verse)) ?? null,
            crossReferences: crossReferencesByVerse.get(Number(verse.verse)) ?? [],
          })),
        });

        this.loadedChapters = nextLoaded;
        this.chapterError = null;
      } catch (error) {
        this.chapterError = this.toErrorMessage(error);
      } finally {
        const nextLoading = new Set(this.loadingChapters);
        nextLoading.delete(index);
        this.loadingChapters = nextLoading;
        this.chapterLoads.delete(index);
        this.schedulePruneChapterCache();
      }
    })();

    this.chapterLoads.set(index, load);
    await load;
  }

  private createStream(
    initialSequence: ReaderStreamSequence,
    initialItemIndex: number,
    initialScrollTop: number,
    preferredId?: string,
  ): ReaderStreamState {
    if (preferredId) {
      this.observeRestoredStreamId(preferredId);
    }

    return new ReaderStreamState(
      preferredId ?? this.nextStreamId(),
      () => this.chapterRefs,
      (index) => this.ensureChapterLoaded(index),
      () => this.schedulePersistWorkspace(),
      () => this.schedulePruneChapterCache(),
      initialSequence,
      initialItemIndex,
      initialScrollTop,
    );
  }

  private async loadCatalog(): Promise<void> {
    const rows = await this.db.query<ChapterCatalogRow>(CATALOG_QUERY);

    const bookMap = new Map<number, BookSummary>();
    const refs: ChapterRef[] = [];
    this.chapterIndexByKey.clear();

    for (const row of rows) {
      const bookId = Number(row.book_id);
      const chapter = Number(row.chapter);

      if (!bookMap.has(bookId)) {
        bookMap.set(bookId, {
          id: bookId,
          name: String(row.book_name),
          testament: row.testament,
          chapterCount: 0,
        });
      }

      const book = bookMap.get(bookId);
      if (!book) continue;
      book.chapterCount += 1;

      const chapterRef: ChapterRef = {
        key: chapterKey(bookId, chapter),
        bookId,
        bookName: book.name,
        testament: row.testament,
        chapter,
        verseCount: Number(row.verse_count),
      };

      this.chapterIndexByKey.set(chapterRef.key, refs.length);
      refs.push(chapterRef);
    }

    this.books = Array.from(bookMap.values());
    this.chapterRefs = refs;
  }

  private defaultIndex(): number {
    const matthewIndex = this.chapterRefs.findIndex(
      (reference) => reference.bookName === 'Matthew' && reference.chapter === 1,
    );

    return matthewIndex >= 0 ? matthewIndex : 0;
  }

  private indexFromQueryParams(): number | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    const book = params.get('book');
    const chapter = Number(params.get('chapter'));

    if (!book || !Number.isInteger(chapter) || chapter < 1) {
      return null;
    }

    const normalizedBook = this.normalizeBookQueryValue(book);
    const index = this.chapterRefs.findIndex(
      (reference) =>
        this.normalizeBookQueryValue(reference.bookName) === normalizedBook &&
        reference.chapter === chapter,
    );

    return index >= 0 ? index : null;
  }

  private resolveInitialWorkspace(snapshot: ReaderWorkspaceSnapshot | null): InitialWorkspaceState {
    const queryIndex = this.indexFromQueryParams();
    const persistedWorkspace = snapshot ? this.resolveSnapshotWorkspace(snapshot) : null;

    if (
      persistedWorkspace &&
      !this.queryReplacesPersistedWorkspace(queryIndex, persistedWorkspace)
    ) {
      return persistedWorkspace;
    }

    if (queryIndex !== null) {
      const sequence = this.canonicalSequence();
      return {
        activeStreamIndex: 0,
        streams: [
          {
            sequence,
            itemIndex: queryIndex,
            catalogIndex: queryIndex,
            align: 'top',
            restoreScrollTop: null,
            streamId: this.nextStreamId(),
          },
        ],
      };
    }

    if (persistedWorkspace) {
      return persistedWorkspace;
    }

    return {
      activeStreamIndex: 0,
      streams: [
        {
          sequence: this.canonicalSequence(),
          itemIndex: this.defaultIndex(),
          catalogIndex: this.defaultIndex(),
          align: 'center',
          restoreScrollTop: null,
          streamId: this.nextStreamId(),
        },
      ],
    };
  }

  private resolveSnapshotWorkspace(snapshot: ReaderWorkspaceSnapshot): InitialWorkspaceState | null {
    if (snapshot.streams.length === 0) {
      return null;
    }

    const resolvedStreams: InitialStreamTarget[] = [];
    let resolvedActiveStreamIndex: number | null = null;
    let fallbackActiveStreamIndex = 0;

    for (const [snapshotIndex, persistedStream] of snapshot.streams.entries()) {
      const resolvedStream = this.resolveSnapshotStream(persistedStream);
      if (!resolvedStream) continue;

      if (snapshotIndex === snapshot.activeStreamIndex) {
        resolvedActiveStreamIndex = resolvedStreams.length;
      }

      if (snapshotIndex <= snapshot.activeStreamIndex) {
        fallbackActiveStreamIndex = resolvedStreams.length;
      }

      resolvedStreams.push(resolvedStream);
    }

    if (resolvedStreams.length === 0) {
      return null;
    }

    return {
      activeStreamIndex:
        resolvedActiveStreamIndex === null ? fallbackActiveStreamIndex : resolvedActiveStreamIndex,
      streams: resolvedStreams,
    };
  }

  private queryReplacesPersistedWorkspace(
    queryIndex: number | null,
    persistedWorkspace: InitialWorkspaceState,
  ): boolean {
    if (queryIndex === null) {
      return false;
    }

    const activeStream = persistedWorkspace.streams[persistedWorkspace.activeStreamIndex];
    return activeStream?.catalogIndex !== queryIndex;
  }

  private resolveSnapshotStream(snapshot: ReaderStreamSnapshot): InitialStreamTarget | null {
    const fallbackCatalogIndex = this.chapterIndexByKey.get(
      chapterKey(snapshot.bookId, snapshot.chapter),
    );
    if (fallbackCatalogIndex === undefined) {
      return null;
    }

    const sequence =
      snapshot.sequence?.source === HORNER_SEQUENCE_SOURCE
        ? this.buildHornerSequence()
        : snapshot.sequence?.kind === 'fixed-list' && Array.isArray(snapshot.sequence.items)
          ? (buildFixedListStreamSequence(this.chapterRefs, this.chapterIndexByKey, {
              title: snapshot.sequence.title,
              source: snapshot.sequence.source,
              items: snapshot.sequence.items,
            }) ?? this.canonicalSequence())
          : this.canonicalSequence();
    if (!sequence) {
      return null;
    }

    const snapshotItemIndex = this.validItemIndex(sequence, snapshot.currentItemIndex);
    const itemIndex =
      sequence.kind === 'fixed-list'
        ? (snapshotItemIndex ?? this.itemIndexForCatalogIndex(sequence, fallbackCatalogIndex) ?? 0)
        : (this.itemIndexForCatalogIndex(sequence, fallbackCatalogIndex) ??
          snapshotItemIndex ??
          fallbackCatalogIndex);
    const item = sequence.items[itemIndex];
    if (!item) {
      return null;
    }

    return {
      sequence,
      itemIndex,
      catalogIndex: item.catalogIndex,
      align: 'top',
      restoreScrollTop: snapshot.scrollTop,
      streamId: snapshot.id,
    };
  }

  private validItemIndex(sequence: ReaderStreamSequence, value: number): number | null {
    return Number.isInteger(value) && value >= 0 && value < sequence.items.length ? value : null;
  }

  private itemIndexForCatalogIndex(
    sequence: ReaderStreamSequence,
    catalogIndex: number,
  ): number | null {
    const itemIndex = sequence.items.findIndex((item) => item.catalogIndex === catalogIndex);
    return itemIndex >= 0 ? itemIndex : null;
  }

  private prepareStreamForCanonicalTarget(stream: ReaderStreamState, catalogIndex: number): number {
    if (stream.sequence.kind !== 'canonical') {
      stream.replaceSequence(this.canonicalSequence(), catalogIndex, 0);
      return catalogIndex;
    }

    stream.activateItemIndex(catalogIndex);
    return catalogIndex;
  }

  private canonicalSequence(): ReaderStreamSequence {
    return buildCanonicalStreamSequence(this.chapterRefs);
  }

  private buildHornerSequence(): ReaderStreamSequence | null {
    return buildFixedListStreamSequence(this.chapterRefs, this.chapterIndexByKey, {
      title: this.hornerTitle(),
      source: HORNER_SEQUENCE_SOURCE,
      items: this.hornerInputForCurrentDay(),
    });
  }

  private hornerInputForCurrentDay(): ChapterListStreamInput[] {
    return buildHornerChapterListStreamInput(this.chapterRefs, this.hornerDay);
  }

  private hornerTitle(): string {
    return `Horner Reading Day ${this.hornerDay}`;
  }

  private hornerKey(bookId: number, chapter: number): string {
    return hornerReadKey(this.hornerDay, bookId, chapter);
  }

  private hornerDayIsComplete(readKeys: ReadonlySet<string>): boolean {
    return resolveHornerReadingsForDay(this.chapterRefs, this.hornerDay).every((reading) =>
      readKeys.has(hornerReadKey(this.hornerDay, reading.bookId, reading.chapter)),
    );
  }

  private replaceHornerStreamSequence(stream: ReaderStreamState): NavigationTarget | null {
    const sequence = this.buildHornerSequence();
    const firstItem = sequence?.items[0];
    const reference = firstItem ? this.chapterRefs[firstItem.catalogIndex] : null;
    if (!sequence || !firstItem || !reference) {
      return null;
    }

    stream.replaceSequence(sequence, 0, 0);
    const target = {
      itemIndex: 0,
      align: 'top' as const,
      targetVerse: null,
    } satisfies NavigationTarget;
    stream.requestNavigation(target, 'auto');

    this.resetDraftSelection(reference.bookId);
    this.replaceLocationToReference(reference, target);
    this.schedulePersistWorkspace();
    this.schedulePruneChapterCache();
    return target;
  }

  private resetDraftSelection(bookId: number | null): void {
    const book = this.books.find((entry) => entry.id === bookId) ?? this.books[0] ?? null;
    this.draftTestament = book?.testament ?? 'NT';
    this.draftBookId = book?.id ?? null;
  }

  private async runVerseSearch(term: string, requestId: number): Promise<void> {
    try {
      const results = await this.searchVerses(term);
      if (requestId !== this.searchRequestId || term !== this.searchQuery.trim()) {
        return;
      }

      this.searchResults = results;
      this.searchBusy = false;
      this.searchError = null;
    } catch (error) {
      if (requestId !== this.searchRequestId) {
        return;
      }

      this.searchResults = [];
      this.searchBusy = false;
      this.searchError = this.toErrorMessage(error);
    }
  }

  private async searchVerses(term: string): Promise<SearchResult[]> {
    const expression = buildVerseSearchExpression(term);
    if (!expression) return [];

    const rows = await this.db.query<SearchRow>(buildVerseSearchQuery(), [expression]);

    return rows.map((row) => ({
      bookId: Number(row.book_id),
      bookName: String(row.book_name),
      chapter: Number(row.chapter),
      verse: Number(row.verse),
      text: String(row.text),
    }));
  }

  private groupCrossReferenceRowsByVerse(rows: CrossReferenceRow[]): Map<number, CrossReference[]> {
    const grouped = new Map<
      number,
      Map<number, CrossReference & { texts: Array<{ verse: number; text: string }> }>
    >();

    for (const row of rows) {
      const fromVerse = Number(row.from_verse);
      const crossReferenceId = Number(row.id);
      const verseGroup = grouped.get(fromVerse) ?? new Map();
      let reference = verseGroup.get(crossReferenceId);

      if (!reference) {
        reference = {
          id: crossReferenceId,
          bookId: Number(row.to_book_id),
          bookName: String(row.to_book_name),
          chapter: Number(row.to_chapter),
          verse: Number(row.to_verse),
          verseEnd:
            row.to_verse_end === null || row.to_verse_end === undefined
              ? null
              : Number(row.to_verse_end),
          label: this.formatCrossReferenceLabel(
            String(row.to_book_name),
            Number(row.to_chapter),
            Number(row.to_verse),
            row.to_verse_end === null || row.to_verse_end === undefined
              ? null
              : Number(row.to_verse_end),
          ),
          text: '',
          texts: [],
        };
        verseGroup.set(crossReferenceId, reference);
        grouped.set(fromVerse, verseGroup);
      }

      if (row.target_verse !== null && row.target_verse !== undefined && row.target_text) {
        reference.texts.push({
          verse: Number(row.target_verse),
          text: String(row.target_text),
        });
      }
    }

    return new Map(
      Array.from(grouped.entries()).map(([fromVerse, references]) => [
        fromVerse,
        Array.from(references.values()).map(({ texts, ...reference }) => ({
          ...reference,
          text:
            texts.length > 0
              ? texts
                  .sort((left, right) => left.verse - right.verse)
                  .map((entry) => entry.text.trim())
                  .join(' ')
              : 'No verse text available for this reference.',
        })),
      ]),
    );
  }

  private groupSourceRowsByVerse(
    rows: SourceWordRow[],
    normalizeToken: (token: string) => string = (token) => token,
  ): Map<number, string> {
    const tokensByVerse = new Map<number, string[]>();

    for (const row of rows) {
      const verse = Number(row.verse);
      const token = normalizeToken(String(row.text).trim()).trim();
      if (!token) continue;

      const tokens = tokensByVerse.get(verse) ?? [];
      tokens.push(token);
      tokensByVerse.set(verse, tokens);
    }

    return new Map(
      Array.from(tokensByVerse.entries()).map(([verse, tokens]) => [verse, tokens.join(' ')]),
    );
  }

  private formatCrossReferenceLabel(
    bookName: string,
    chapter: number,
    verse: number,
    verseEnd: number | null,
  ): string {
    if (verseEnd !== null && verseEnd > verse) {
      return `${bookName} ${chapter}:${verse}-${verseEnd}`;
    }

    return `${bookName} ${chapter}:${verse}`;
  }

  private restoreWorkspaceFromHistory(target: ReaderHistoryState): NavigationTarget | null {
    const targetIndex = this.chapterIndexByKey.get(chapterKey(target.bookId, target.chapter));
    if (targetIndex === undefined) {
      return null;
    }

    if (target.streams.length <= 1) {
      return this.restoreSingleStreamFromHistory(target, targetIndex);
    }

    const resolvedWorkspace = this.resolveHistoryWorkspace(target, targetIndex);
    const nextStreams = resolvedWorkspace.streams.map((streamTarget) =>
      this.createStream(
        streamTarget.sequence,
        streamTarget.itemIndex,
        streamTarget.restoreScrollTop ?? 0,
        streamTarget.streamId,
      ),
    );
    const nextActiveStreamIndex = Math.min(
      Math.max(0, resolvedWorkspace.activeStreamIndex),
      Math.max(0, nextStreams.length - 1),
    );
    const activeStream = nextStreams[nextActiveStreamIndex] ?? null;
    if (!activeStream) {
      return null;
    }

    for (const stream of this.streams) {
      stream.destroy();
    }

    this.streams = nextStreams;
    this.activeStreamIndex = nextActiveStreamIndex;

    for (const [streamIndex, stream] of nextStreams.entries()) {
      const streamTarget = resolvedWorkspace.streams[streamIndex];
      if (!streamTarget || streamIndex === nextActiveStreamIndex) {
        continue;
      }

      stream.activateItemIndex(streamTarget.itemIndex);
      stream.requestNavigation(
        {
          itemIndex: streamTarget.itemIndex,
          align: streamTarget.align,
          targetVerse: null,
        },
        'auto',
        {
          restoreScrollTop: streamTarget.restoreScrollTop,
        },
      );
    }

    const activeStreamTarget = resolvedWorkspace.streams[nextActiveStreamIndex];
    const activeItemIndex =
      activeStream.sequence.kind === 'fixed-list' &&
      activeStreamTarget?.catalogIndex === targetIndex
        ? activeStreamTarget.itemIndex
        : (this.itemIndexForCatalogIndex(activeStream.sequence, targetIndex) ??
          activeStreamTarget?.itemIndex ??
          0);
    activeStream.activateItemIndex(activeItemIndex);
    activeStream.requestNavigation(
      {
        itemIndex: activeItemIndex,
        align: target.align,
        targetVerse: target.restoreMode === 'target' ? target.targetVerse : null,
      },
      'auto',
      {
        restoreScrollTop:
          target.restoreMode === 'scroll'
            ? (resolvedWorkspace.streams[nextActiveStreamIndex]?.restoreScrollTop ?? null)
            : null,
      },
    );
    if (target.targetVerse !== null) {
      activeStream.highlightVerse(target.bookId, target.chapter, target.targetVerse);
    }

    this.resetDraftSelection(target.bookId);
    this.chapterError = null;
    this.schedulePersistWorkspace();
    return {
      itemIndex: activeItemIndex,
      align: target.align,
      targetVerse: target.targetVerse,
    };
  }

  private restoreSingleStreamFromHistory(
    target: ReaderHistoryState,
    targetIndex: number,
  ): NavigationTarget | null {
    const preferredId = target.streams[0]?.id ?? this.activeStream?.id ?? this.nextStreamId();
    const restoreScrollTop =
      target.restoreMode === 'scroll' ? (target.streams[0]?.scrollTop ?? null) : null;
    const resolvedStream = target.streams[0] ? this.resolveSnapshotStream(target.streams[0]) : null;
    const sequence =
      resolvedStream && this.itemIndexForCatalogIndex(resolvedStream.sequence, targetIndex) !== null
        ? resolvedStream.sequence
        : this.canonicalSequence();
    const itemIndex = this.itemIndexForCatalogIndex(sequence, targetIndex) ?? targetIndex;
    const nextStream = this.createStream(sequence, itemIndex, restoreScrollTop ?? 0, preferredId);

    for (const stream of this.streams) {
      stream.destroy();
    }

    this.streams = [nextStream];
    this.activeStreamIndex = 0;
    nextStream.activateItemIndex(itemIndex);
    nextStream.requestNavigation(
      {
        itemIndex,
        align: target.align,
        targetVerse: target.restoreMode === 'target' ? target.targetVerse : null,
      },
      'auto',
      {
        restoreScrollTop,
      },
    );

    if (target.targetVerse !== null) {
      nextStream.highlightVerse(target.bookId, target.chapter, target.targetVerse);
    }

    this.resetDraftSelection(target.bookId);
    this.chapterError = null;
    this.schedulePersistWorkspace();
    return {
      itemIndex,
      align: target.align,
      targetVerse: target.targetVerse,
    };
  }

  private resolveHistoryWorkspace(
    target: ReaderHistoryState,
    fallbackIndex: number,
  ): InitialWorkspaceState {
    if (target.streams.length === 0) {
      return {
        activeStreamIndex: 0,
        streams: [
          {
            sequence: this.canonicalSequence(),
            itemIndex: fallbackIndex,
            catalogIndex: fallbackIndex,
            align: target.align,
            restoreScrollTop: null,
            streamId: this.activeStream?.id ?? this.nextStreamId(),
          },
        ],
      };
    }

    const resolvedStreams: InitialStreamTarget[] = [];
    let resolvedActiveStreamIndex: number | null = null;
    let fallbackActiveStreamIndex = 0;

    for (const [snapshotIndex, persistedStream] of target.streams.entries()) {
      const resolvedStream = this.resolveSnapshotStream(persistedStream);
      if (!resolvedStream) continue;

      if (snapshotIndex === target.activeStreamIndex) {
        resolvedActiveStreamIndex = resolvedStreams.length;
      }

      if (snapshotIndex <= target.activeStreamIndex) {
        fallbackActiveStreamIndex = resolvedStreams.length;
      }

      resolvedStreams.push(resolvedStream);
    }

    if (resolvedStreams.length === 0) {
      return {
        activeStreamIndex: 0,
        streams: [
          {
            sequence: this.canonicalSequence(),
            itemIndex: fallbackIndex,
            catalogIndex: fallbackIndex,
            align: target.align,
            restoreScrollTop: null,
            streamId: this.activeStream?.id ?? this.nextStreamId(),
          },
        ],
      };
    }

    return {
      activeStreamIndex:
        resolvedActiveStreamIndex === null ? fallbackActiveStreamIndex : resolvedActiveStreamIndex,
      streams: resolvedStreams,
    };
  }

  private pushLocationToReference(
    reference: ChapterRef,
    options: { targetVerse: number | null; align: ScrollAlign },
  ): void {
    if (typeof history === 'undefined') {
      return;
    }

    history.pushState(
      this.buildHistoryState(reference, options, 'target'),
      '',
      this.buildReferenceUrl(reference),
    );
  }

  private replaceLocationToReference(
    reference: ChapterRef,
    options: { targetVerse: number | null; align: ScrollAlign },
  ): void {
    if (typeof window === 'undefined' || typeof history === 'undefined') {
      return;
    }

    history.replaceState(
      this.buildHistoryState(reference, options, 'scroll'),
      '',
      this.buildReferenceUrl(reference),
    );
  }

  private buildReferenceUrl(reference: ChapterRef): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const params = new URLSearchParams(window.location.search);
    params.set('book', reference.bookName);
    params.set('chapter', String(reference.chapter));

    const nextSearch = params.toString();
    return `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
  }

  private buildHistoryState(
    reference: ChapterRef,
    options: { targetVerse: number | null; align: ScrollAlign },
    restoreMode: ReaderHistoryState['restoreMode'],
  ): ReaderHistoryState {
    const snapshot = this.buildWorkspaceSnapshot();
    const fallbackStreamId = this.activeStream?.id ?? this.nextStreamId();
    const historySnapshot =
      snapshot ??
      ({
        version: READER_WORKSPACE_SNAPSHOT_VERSION,
        activeStreamIndex: 0,
        streams: [
          {
            id: fallbackStreamId,
            bookId: reference.bookId,
            chapter: reference.chapter,
            currentItemIndex:
              this.chapterIndexByKey.get(chapterKey(reference.bookId, reference.chapter)) ?? 0,
            sequence: {
              kind: 'canonical',
              title: null,
              source: null,
            },
            scrollTop: 0,
          },
        ],
      } satisfies ReaderWorkspaceSnapshot);

    return {
      kjvreader: true,
      restoreMode,
      activeStreamIndex: historySnapshot.activeStreamIndex,
      streams: historySnapshot.streams,
      bookId: reference.bookId,
      bookName: reference.bookName,
      chapter: reference.chapter,
      targetVerse: options.targetVerse,
      align: options.align,
    };
  }

  private historyTargetFromState(state: unknown): ReaderHistoryState | null {
    if (
      !state ||
      typeof state !== 'object' ||
      (state as { kjvreader?: unknown }).kjvreader !== true
    ) {
      return null;
    }

    const candidate = state as Partial<ReaderHistoryState>;
    if (
      typeof candidate.bookId !== 'number' ||
      typeof candidate.bookName !== 'string' ||
      typeof candidate.chapter !== 'number'
    ) {
      return null;
    }

    const streams = Array.isArray(candidate.streams)
      ? candidate.streams
          .map((stream) => this.normalizeHistoryStreamSnapshot(stream))
          .filter((stream): stream is ReaderStreamSnapshot => stream !== null)
      : [];

    return {
      kjvreader: true,
      restoreMode: candidate.restoreMode === 'target' ? 'target' : 'scroll',
      activeStreamIndex:
        typeof candidate.activeStreamIndex === 'number' && candidate.activeStreamIndex >= 0
          ? candidate.activeStreamIndex
          : 0,
      streams,
      bookId: candidate.bookId,
      bookName: candidate.bookName,
      chapter: candidate.chapter,
      targetVerse: typeof candidate.targetVerse === 'number' ? candidate.targetVerse : null,
      align: candidate.align === 'top' ? 'top' : 'center',
    };
  }

  private normalizeHistoryStreamSnapshot(value: unknown): ReaderStreamSnapshot | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const candidate = value as Record<string, unknown>;
    if (
      typeof candidate['id'] !== 'string' ||
      typeof candidate['bookId'] !== 'number' ||
      typeof candidate['chapter'] !== 'number' ||
      typeof candidate['scrollTop'] !== 'number'
    ) {
      return null;
    }

    const sequence =
      candidate['sequence'] && typeof candidate['sequence'] === 'object'
        ? (candidate['sequence'] as ReaderStreamSnapshot['sequence'])
        : {
            kind: 'canonical' as const,
            title: null,
            source: null,
          };

    return {
      id: candidate['id'],
      bookId: candidate['bookId'],
      chapter: candidate['chapter'],
      currentItemIndex:
        typeof candidate['currentItemIndex'] === 'number'
          ? candidate['currentItemIndex']
          : (this.chapterIndexByKey.get(chapterKey(candidate['bookId'], candidate['chapter'])) ??
            0),
      sequence,
      scrollTop: candidate['scrollTop'],
    };
  }

  private historyTargetFromLocation(): ReaderHistoryState | null {
    const index = this.indexFromQueryParams();
    if (index === null) return null;

    const reference = this.chapterRefs[index];
    if (!reference) return null;

    return {
      kjvreader: true,
      restoreMode: 'target',
      activeStreamIndex: 0,
      streams: [
        {
          id: this.activeStream?.id ?? this.nextStreamId(),
          bookId: reference.bookId,
          chapter: reference.chapter,
          currentItemIndex: index,
          sequence: {
            kind: 'canonical',
            title: null,
            source: null,
          },
          scrollTop: 0,
        },
      ],
      bookId: reference.bookId,
      bookName: reference.bookName,
      chapter: reference.chapter,
      targetVerse: null,
      align: 'top',
    };
  }

  private normalizeBookQueryValue(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private readStoredNavHandedness(): NavHandedness {
    if (typeof localStorage === 'undefined') {
      return 'right';
    }

    return localStorage.getItem(this.navHandednessStorageKey) === 'left' ? 'left' : 'right';
  }

  private setUpdateStatus(status: UpdateCheckState): void {
    if (this.updateStatusClearHandle !== null) {
      clearTimeout(this.updateStatusClearHandle);
      this.updateStatusClearHandle = null;
    }

    this.updateStatus = status;
    if (status.state !== 'up-to-date') {
      return;
    }

    this.updateStatusClearHandle = setTimeout(() => {
      if (this.updateStatus.state === 'up-to-date') {
        this.updateStatus = { state: 'idle' };
      }
      this.updateStatusClearHandle = null;
    }, 4000);
  }

  private schedulePersistWorkspace(delay = 120): void {
    if (this.phase !== 'ready') {
      return;
    }

    if (this.persistHandle !== null) {
      clearTimeout(this.persistHandle);
    }

    this.persistHandle = setTimeout(() => {
      this.persistHandle = null;
      void this.persistWorkspace();
    }, delay);
  }

  private schedulePruneChapterCache(delay = 250): void {
    if (this.pruneHandle !== null) {
      clearTimeout(this.pruneHandle);
    }

    this.pruneHandle = setTimeout(() => {
      this.pruneHandle = null;
      this.pruneChapterCache();
    }, delay);
  }

  private async persistWorkspace(): Promise<void> {
    const snapshot = this.buildWorkspaceSnapshot();
    if (!snapshot) {
      return;
    }

    try {
      await this.storage.save(snapshot);
    } catch {
      // Ignore storage errors; the reader must remain usable without persistence.
    }
  }

  private async readPersistedSnapshot(): Promise<ReaderWorkspaceSnapshot | null> {
    try {
      return await this.storage.load();
    } catch {
      return null;
    }
  }

  private async loadHornerProgress(): Promise<void> {
    if (!this.storage.loadHornerProgress) {
      return;
    }

    try {
      const progress = await this.storage.loadHornerProgress();
      if (!progress) return;

      this.hornerDay = progress.day;
      this.hornerReadKeys = new Set(this.validHornerReadKeys(progress));
    } catch {
      this.hornerDay = 1;
      this.hornerReadKeys = new Set();
    }
  }

  private persistHornerProgress(): void {
    if (!this.storage.saveHornerProgress) {
      return;
    }

    const progress: HornerProgressState = {
      day: this.hornerDay,
      readKeys: Array.from(this.hornerReadKeys.values()),
    };

    void this.storage.saveHornerProgress(progress).catch(() => {
      // Ignore storage errors; Horner progress should not block the reader.
    });
  }

  private validHornerReadKeys(progress: HornerProgressState): string[] {
    const currentDayPrefix = `${progress.day}:`;
    return progress.readKeys.filter((key) => key.startsWith(currentDayPrefix));
  }

  private buildWorkspaceSnapshot(): ReaderWorkspaceSnapshot | null {
    const streams = this.streams
      .map((stream) => stream.snapshot())
      .filter((snapshot): snapshot is NonNullable<typeof snapshot> => snapshot !== null);

    if (streams.length === 0) {
      return null;
    }

    return {
      version: READER_WORKSPACE_SNAPSHOT_VERSION,
      activeStreamIndex: Math.min(this.activeStreamIndex, streams.length - 1),
      streams,
    };
  }

  private pruneChapterCache(): void {
    if (this.loadedChapters.size === 0) {
      return;
    }

    const keep = new Set<number>();

    for (const stream of this.streams) {
      if (stream.currentCatalogIndex !== null) {
        if (stream.sequence.kind === 'canonical') {
          for (
            let index = stream.currentCatalogIndex - CACHE_RADIUS;
            index <= stream.currentCatalogIndex + CACHE_RADIUS;
            index += 1
          ) {
            if (index >= 0 && index < this.chapterRefs.length) {
              keep.add(index);
            }
          }
        } else {
          keep.add(stream.currentCatalogIndex);
        }
      }

      for (const index of stream.visible) {
        const catalogIndex = stream.sequence.items[index]?.catalogIndex;
        if (catalogIndex !== undefined) {
          keep.add(catalogIndex);
        }
      }
    }

    const streamCount = Math.max(1, this.streams.length);
    const minimumLoadedBeforePrune = keep.size + CACHE_PRUNE_HEADROOM * streamCount;

    if (
      keep.size === 0 ||
      keep.size >= this.loadedChapters.size ||
      this.loadedChapters.size <= minimumLoadedBeforePrune
    ) {
      return;
    }

    const nextLoaded = new Map<number, ChapterData>();
    for (const [index, chapter] of this.loadedChapters) {
      if (keep.has(index)) {
        nextLoaded.set(index, chapter);
      }
    }

    if (nextLoaded.size !== this.loadedChapters.size) {
      this.loadedChapters = nextLoaded;
    }
  }

  private nextStreamId(): string {
    this.streamIdNonce += 1;
    return `stream-${this.streamIdNonce}`;
  }

  private observeRestoredStreamId(streamId: string): void {
    const match = /^stream-(\d+)$/.exec(streamId);
    if (!match) {
      return;
    }

    const parsedNonce = Number(match[1]);
    if (Number.isInteger(parsedNonce) && parsedNonce > this.streamIdNonce) {
      this.streamIdNonce = parsedNonce;
    }
  }

  private toErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }
}
